import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Dumbbell, MapPin, X, Plus, Trash2, Check, Minus, ChevronUp, ChevronDown, Calendar as CalendarIcon, Footprints } from "lucide-react";
import { PlatformMap } from "./PlatformMap";
import { Workout, Exercise, useWorkouts } from "@/hooks/useWorkouts";
import { supabase } from "@/integrations/supabase/client";
import { ExerciseProgressModal } from "./ExerciseProgressModal";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";
import { Analytics } from "@/lib/analytics";
import { useTranslation } from "react-i18next";
import {
  isGpsCardioWorkoutType,
  findGpsCardioExercise,
  exerciseNameFromWorkoutType,
  GPS_CARDIO_EXERCISE_NAMES,
  type GpsCardioExerciseName,
} from "@/lib/gpsCardioWorkout";
import { useUnitFormatters } from "@/hooks/useUnits";
import { displayWeightToKg, kgToDisplayWeight } from "@/lib/units";

// Individual set structure for editing
interface EditWorkoutSet {
  id: string;
  reps: number | null;
  weight: number | null;
  completed: boolean;
}

// Exercise with individual sets for editing  
interface EditWorkoutExercise {
  name: string;
  sets: EditWorkoutSet[];
  notes?: string;
  muscle_groups: string[];
  equipment?: string;
  originalIds: string[]; // Track original exercise IDs from database
}

interface EditWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: Workout | null;
}

export const EditWorkoutModal = ({ isOpen, onClose, workout }: EditWorkoutModalProps) => {
  const { t } = useTranslation();
  const { units, weightLabel } = useUnitFormatters();
  const { updateWorkout, deleteWorkout, addExerciseToWorkout, updateExercise, deleteExercise, refetch } = useWorkouts();
  
  // Workout details state
  const [title, setTitle] = useState("");
  const [workoutType, setWorkoutType] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [notes, setNotes] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [caloriesBurned, setCaloriesBurned] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState<Date>();
  const [endTime, setEndTime] = useState("");
  
  // Exercises state - now using the new structure
  const [exercises, setExercises] = useState<EditWorkoutExercise[]>([]);
  const [newExercise, setNewExercise] = useState({
    name: "",
    sets: "",
    reps: "",
    weight_kg: "",
    distance_km: "",
    equipment: "",
    muscle_groups: [] as string[],
    notes: ""
  });
  
  // Running workout state
  const [totalDistance, setTotalDistance] = useState("");
  const [totalTime, setTotalTime] = useState("");
  
  const [saving, setSaving] = useState(false);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedExerciseForProgress, setSelectedExerciseForProgress] = useState("");

  // Initialize form with workout data
  useEffect(() => {
    if (workout && isOpen) {
      setTitle(workout.title);
      setWorkoutType(workout.workout_type || "");
      setDifficulty(workout.difficulty_level || "medium");
      setNotes(workout.notes || "");
      setDurationMinutes(workout.duration_minutes?.toString() || "");
      setCaloriesBurned(workout.calories_burned?.toString() || "");
      
      // Initialize start date/time
      if (workout.started_at) {
        const startDateTime = new Date(workout.started_at);
        setStartDate(startDateTime);
        setStartTime(format(startDateTime, "HH:mm"));
      }
      
      // Initialize end date/time
      if (workout.completed_at) {
        const endDateTime = new Date(workout.completed_at);
        setEndDate(endDateTime);
        setEndTime(format(endDateTime, "HH:mm"));
      }
      
      // Convert exercises to the new structure - group by exercise name
      // IMPORTANT: Use set_details JSON array when available (new format)
      if (workout.exercises && workout.exercises.length > 0) {
        const groupedExercises = workout.exercises.reduce((acc, exercise) => {
          if (GPS_CARDIO_EXERCISE_NAMES.includes(exercise.name as GpsCardioExerciseName)) return acc;
          
          // Cast to access set_details which exists in DB but not in Exercise type
          const exerciseWithDetails = exercise as Exercise & { set_details?: any[] };
          
          if (!acc[exercise.name]) {
            acc[exercise.name] = {
              name: exercise.name,
              sets: [],
              notes: exercise.notes || "",
              muscle_groups: exercise.muscle_groups || [],
              equipment: exercise.equipment || "",
              originalIds: []
            };
          }
          
          // Check if set_details is available (new format) - it contains all sets as JSON array
          const setDetails = exerciseWithDetails.set_details;
          if (setDetails && Array.isArray(setDetails) && setDetails.length > 0) {
            // Parse set_details JSON array - this has all the sets
            setDetails.forEach((setDetail: any) => {
              const kg = setDetail.weight_kg ?? null;
              acc[exercise.name].sets.push({
                id: crypto.randomUUID(),
                reps: setDetail.reps || null,
                weight: kg != null ? Math.round(kgToDisplayWeight(kg, units) * 10) / 10 : null,
                completed: setDetail.completed !== false // Default to true for existing sets
              });
            });
          } else {
            // Fallback to legacy format (one row per set)
            const kg = exercise.weight_kg ?? null;
            acc[exercise.name].sets.push({
              id: exercise.id || crypto.randomUUID(),
              reps: exercise.reps,
              weight: kg != null ? Math.round(kgToDisplayWeight(kg, units) * 10) / 10 : null,
              completed: true // All existing sets are considered completed
            });
          }
          
          acc[exercise.name].originalIds.push(exercise.id || "");
          
          return acc;
        }, {} as Record<string, EditWorkoutExercise>);
        
        setExercises(Object.values(groupedExercises));
      } else {
        setExercises([]);
      }
      
      // For GPS outdoor workouts, extract distance and time from the cardio exercise row
      if (isGpsCardioWorkoutType(workout.workout_type) && workout.exercises?.length > 0) {
        const runningExercise = findGpsCardioExercise(workout.exercises);
        if (runningExercise) {
          setTotalDistance(runningExercise.distance_km?.toString() || "");
          if (runningExercise.duration_seconds) {
            const hours = Math.floor(runningExercise.duration_seconds / 3600);
            const minutes = Math.floor((runningExercise.duration_seconds % 3600) / 60);
            const seconds = runningExercise.duration_seconds % 60;
            setTotalTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
          }
        }
      }
    }
  }, [workout, isOpen, units]);

  const resetForm = () => {
    setTitle("");
    setWorkoutType("");
    setDifficulty("medium");
    setNotes("");
    setDurationMinutes("");
    setCaloriesBurned("");
    setStartDate(undefined);
    setStartTime("");
    setEndDate(undefined);
    setEndTime("");
    setExercises([]);
    setTotalDistance("");
    setTotalTime("");
    setNewExercise({
      name: "",
      sets: "",
      reps: "",
      weight_kg: "",
      distance_km: "",
      equipment: "",
      muscle_groups: [],
      notes: ""
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const convertTimeToSeconds = (timeStr: string) => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
  };

  const addExercise = () => {
    if (!newExercise.name.trim()) {
      toast.error(t('components.editWorkout.toast.exerciseNameRequired'));
      return;
    }

    const newWorkoutExercise: EditWorkoutExercise = {
      name: newExercise.name.trim(),
      sets: [{ 
        id: crypto.randomUUID(), 
        reps: newExercise.reps ? parseInt(newExercise.reps) : null, 
        weight: newExercise.weight_kg ? parseFloat(newExercise.weight_kg) : null, 
        completed: true 
      }],
      notes: newExercise.notes || "",
      muscle_groups: newExercise.muscle_groups,
      equipment: newExercise.equipment || "",
      originalIds: []
    };

    setExercises(prev => [...prev, newWorkoutExercise]);
    setNewExercise({
      name: "",
      sets: "",
      reps: "",
      weight_kg: "",
      distance_km: "",
      equipment: "",
      muscle_groups: [],
      notes: ""
    });
  };

  const removeExercise = (exerciseIndex: number) => {
    setExercises(prev => prev.filter((_, index) => index !== exerciseIndex));
  };

  const moveExercise = (exerciseIndex: number, direction: 'up' | 'down') => {
    const newExercises = [...exercises];
    const targetIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newExercises.length) return;
    
    // Swap exercises
    [newExercises[exerciseIndex], newExercises[targetIndex]] = 
    [newExercises[targetIndex], newExercises[exerciseIndex]];
    
    setExercises(newExercises);
  };

  // Add set to an exercise
  const addSet = (exerciseIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex 
        ? {
            ...exercise,
            sets: [...exercise.sets, {
              id: crypto.randomUUID(),
              reps: null,
              weight: null,
              completed: true
            }]
          }
        : exercise
    ));
  };

  // Remove set from an exercise
  const removeSet = (exerciseIndex: number, setIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex && exercise.sets.length > 1
        ? {
            ...exercise,
            sets: exercise.sets.filter((_, sIndex) => sIndex !== setIndex)
          }
        : exercise
    ));
  };

  // Update set data
  const updateSet = (exerciseIndex: number, setIndex: number, field: 'reps' | 'weight', value: number | null) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex
        ? {
            ...exercise,
            sets: exercise.sets.map((set, sIndex) => 
              sIndex === setIndex
                ? { ...set, [field]: value }
                : set
            )
          }
        : exercise
    ));
  };

  // Toggle set completion
  const completeSet = (exerciseIndex: number, setIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex
        ? {
            ...exercise,
            sets: exercise.sets.map((set, sIndex) => 
              sIndex === setIndex
                ? { ...set, completed: !set.completed }
                : set
            )
          }
        : exercise
    ));
  };

  const handleMuscleGroupToggle = (group: string) => {
    setNewExercise(prev => ({
      ...prev,
      muscle_groups: prev.muscle_groups.includes(group)
        ? prev.muscle_groups.filter(g => g !== group)
        : [...prev.muscle_groups, group]
    }));
  };

  const saveWorkout = async () => {
    if (!workout || !title.trim()) {
      toast.error(t('components.editWorkout.toast.titleRequired'));
      return;
    }

    setSaving(true);
    try {
      // Build started_at timestamp if date and time are provided
      let startedAt = null;
      if (startDate && startTime) {
        const [hours, minutes] = startTime.split(':');
        const startDateTime = new Date(startDate);
        startDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        startedAt = startDateTime.toISOString();
      }

      // Build completed_at timestamp if date and time are provided
      let completedAt = null;
      if (endDate && endTime) {
        const [hours, minutes] = endTime.split(':');
        const endDateTime = new Date(endDate);
        endDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        completedAt = endDateTime.toISOString();
      }

      // Update workout details
      const workoutData = {
        title: title.trim(),
        workout_type: workoutType,
        difficulty_level: difficulty,
        duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
        calories_burned: caloriesBurned ? parseInt(caloriesBurned) : null,
        notes: notes.trim() || null,
        started_at: startedAt,
        completed_at: completedAt
      };

      const { error: workoutError } = await updateWorkout(workout.id!, workoutData, { skipRefresh: true });
      
      if (workoutError) {
        throw new Error('Failed to update workout');
      }

      // Update exercises for strength workouts (keep GPS cardio row if present)
      if (!isGpsCardioWorkoutType(workoutType)) {
        const { data: existingRows, error: selErr } = await supabase
          .from("exercises")
          .select("id,name")
          .eq("workout_id", workout.id!);
        if (selErr) throw selErr;
        for (const row of existingRows || []) {
          if (!GPS_CARDIO_EXERCISE_NAMES.includes(row.name as GpsCardioExerciseName)) {
            const { error: delOne } = await supabase.from("exercises").delete().eq("id", row.id);
            if (delOne) throw delOne;
          }
        }


        // Now add all exercises with set_details (single row per exercise with all sets in JSON)
        for (const exercise of exercises) {
          const completedSets = exercise.sets.filter(set => set.completed);
          if (completedSets.length === 0) continue;

          // Build set_details JSON array with all sets
          const setDetails = completedSets.map((set, idx) => ({
            set_number: idx + 1,
            reps: set.reps,
            weight_kg: set.weight != null ? displayWeightToKg(set.weight, units) : null,
            completed: set.completed
          }));

          // Use the first completed set for the main reps/weight columns (for backwards compat)
          const firstSet = completedSets[0];

          const { error } = await addExerciseToWorkout(
            workout.id!,
            {
              name: exercise.name,
              sets: completedSets.length, // Total number of sets
              reps: firstSet.reps,
              weight_kg: firstSet.weight != null ? displayWeightToKg(firstSet.weight, units) : null,
              equipment: exercise.equipment || null,
              muscle_groups: exercise.muscle_groups,
              notes: exercise.notes || null,
              duration_seconds: null,
              rest_seconds: null,
              distance_km: null,
              set_details: setDetails // All sets stored in JSON array
            },
            { skipRefresh: true }
          );
          if (error) throw error;
        }
      }

      // Handle GPS cardio (run / walk / hike) workout
      if (isGpsCardioWorkoutType(workoutType)) {
        const exName = exerciseNameFromWorkoutType(workoutType);
        const runningExercise = {
          name: exName,
          distance_km: totalDistance ? parseFloat(totalDistance) : null,
          duration_seconds: totalTime ? convertTimeToSeconds(totalTime) : null,
          notes: null,
          muscle_groups: ['legs', 'cardiovascular'],
          equipment: 'none',
          sets: null,
          reps: null,
          weight_kg: null,
          rest_seconds: null
        };

        const existingGps = findGpsCardioExercise(workout.exercises);
        if (existingGps) {
          const { error } = await updateExercise(existingGps.id!, runningExercise, { skipRefresh: true });
          if (error) throw error;
        } else {
          const { error } = await addExerciseToWorkout(workout.id!, runningExercise, { skipRefresh: true });
          if (error) throw error;
        }
      }

      await refetch();
      Analytics.workoutEdited(workout.id!, title.trim());
      toast.success(t('components.editWorkout.toast.saveSuccess'));
      handleClose();
    } catch (error) {
      console.error('Error updating workout:', error);
      toast.error(t('components.editWorkout.toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkout = async () => {
    if (!workout) return;
    
    try {
      const { error } = await deleteWorkout(workout.id!);
      if (error) {
        throw new Error('Failed to delete workout');
      }
      
      Analytics.workoutDeleted(workout.id!, workout.title || 'Untitled Workout');
      toast.success(t('components.editWorkout.toast.deleteSuccess'));
      handleClose();
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error(t('components.editWorkout.toast.deleteError'));
    }
  };

  if (!workout) return null;

  const muscleGroups = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs', 'glutes', 'core', 'cardio'];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            {workoutType === 'running' ? (
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            ) : (
              <Dumbbell className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            )}
            {t('components.editWorkout.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Basic Workout Details */}
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg font-normal">{t('components.editWorkout.workoutDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">{t('components.editWorkout.titleLabel')}</Label>
                  <Input
                    id="edit-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-type">{t('components.editWorkout.typeLabel')}</Label>
                  <Select value={workoutType} onValueChange={setWorkoutType}>
                    <SelectTrigger className="bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strength">{t('components.editWorkout.type.strength')}</SelectItem>
                      <SelectItem value="cardio">{t('components.editWorkout.type.cardio')}</SelectItem>
                      <SelectItem value="running">{t('components.editWorkout.type.running')}</SelectItem>
                      <SelectItem value="yoga">{t('components.editWorkout.type.yoga')}</SelectItem>
                      <SelectItem value="mixed">{t('components.editWorkout.type.mixed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-difficulty" className="text-sm">{t('components.editWorkout.difficultyLabel')}</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger className="bg-background/50 h-9 sm:h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">{t('components.editWorkout.difficulty.easy')}</SelectItem>
                      <SelectItem value="medium">{t('components.editWorkout.difficulty.medium')}</SelectItem>
                      <SelectItem value="hard">{t('components.editWorkout.difficulty.hard')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-duration" className="text-sm">{t('components.editWorkout.durationLabel')}</Label>
                  <Input
                    id="edit-duration"
                    type="number"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className="bg-background/50 h-9 sm:h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-calories" className="text-sm">{t('components.editWorkout.caloriesLabel')}</Label>
                  <Input
                    id="edit-calories"
                    type="number"
                    value={caloriesBurned}
                    onChange={(e) => setCaloriesBurned(e.target.value)}
                    className="bg-background/50 h-9 sm:h-10"
                  />
                </div>
              </div>

              {/* Start and End Time for Completed Workouts */}
              {workout.completed_at && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('components.editWorkout.startTime')}</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal bg-background/50 flex-1",
                              !startDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {startDate ? format(startDate, "MMM d, yyyy") : t('components.editWorkout.pickDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={setStartDate}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="bg-background/50 w-32"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">{t('components.editWorkout.endTime')}</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal bg-background/50 flex-1",
                              !endDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {endDate ? format(endDate, "MMM d, yyyy") : t('components.editWorkout.pickDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={endDate}
                            onSelect={setEndDate}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="bg-background/50 w-32"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* GPS cardio (run / walk / hike) rich summary */}
          {isGpsCardioWorkoutType(workoutType) && (() => {
            const runningExercise = findGpsCardioExercise(workout.exercises);
            let runData: any = null;
            if (runningExercise?.notes) {
              try { runData = JSON.parse(runningExercise.notes); } catch {}
            }
            const distKm = runningExercise?.distance_km;
            const durSec = runningExercise?.duration_seconds;
            const pace = runData?.pace;
            const splits: any[] = runData?.splits || [];
            const routePoints: { lat: number; lng: number }[] = runData?.routePoints || [];
            const routeLine: [number, number][] = routePoints.map(p => [p.lat, p.lng]);
            const feeling = runData?.feeling;
            const avgSpeed = runData?.avgSpeedKmh;
            const pauseCount = runData?.pauseCount;

            const formatRunTime = (sec: number) => {
              const h = Math.floor(sec / 3600);
              const m = Math.floor((sec % 3600) / 60);
              const s = sec % 60;
              return h > 0 
                ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
                : `${m}:${s.toString().padStart(2, '0')}`;
            };

            return (
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg font-normal flex items-center gap-2">
                    <Footprints className="h-4 w-4 text-primary" />
                    {t('components.editWorkout.runningDetails')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
                  {/* Route Map */}
                  {routeLine.length > 1 && (
                    <div className="rounded-xl overflow-hidden border border-border/30 h-48">
                      <PlatformMap
                        center={routeLine[Math.floor(routeLine.length / 2)]}
                        routePoints={routeLine}
                        showUserMarker={false}
                        showRoute={true}
                        showStartFinishMarkers={true}
                        showKmMarkers={true}
                        fitToRoute={true}
                        autoFollow={false}
                        className="w-full h-full"
                        darkMode={true}
                        interactive={true}
                        zoom={13}
                      />
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm bg-muted/30 rounded-xl p-4 border border-border/30">
                    <div className="text-center">
                      <div className="font-bold text-lg text-primary">{distKm ?? '—'} km</div>
                      <div className="text-xs text-muted-foreground">{t('components.editWorkout.distance')}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{durSec ? formatRunTime(durSec) : (durationMinutes ? `${durationMinutes}min` : '—')}</div>
                      <div className="text-xs text-muted-foreground">{t('components.editWorkout.totalTime')}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{pace || '—'}</div>
                      <div className="text-xs text-muted-foreground">{t('components.editWorkout.avgPace')}</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{caloriesBurned || '—'}</div>
                      <div className="text-xs text-muted-foreground">{t('components.editWorkout.calories')}</div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  {(avgSpeed || feeling || pauseCount !== undefined) && (
                    <div className="flex flex-wrap gap-2">
                      {avgSpeed && (
                        <Badge variant="outline" className="text-xs">{t('components.editWorkout.avgSpeed', { speed: avgSpeed })}</Badge>
                      )}
                      {feeling && (
                        <Badge variant="outline" className="text-xs">{t('components.editWorkout.feelingLabel', { feeling })}</Badge>
                      )}
                      {pauseCount !== undefined && pauseCount > 0 && (
                        <Badge variant="outline" className="text-xs">{t('components.editWorkout.pauses', { count: pauseCount })}</Badge>
                      )}
                    </div>
                  )}

                  {/* Km Splits */}
                  {splits.length > 0 && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-2 block font-medium">{t('components.editWorkout.kilometerSplits')}</label>
                      <div className="space-y-1 max-h-40 overflow-y-auto bg-muted/20 rounded-lg p-2">
                        {splits.map((split: any, idx: number) => (
                          <div key={idx} className="flex justify-between items-center text-sm px-2 py-1.5 rounded-md hover:bg-muted/20">
                            <span className="text-muted-foreground font-medium">
                              {split.km} km
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">{split.time}</span>
                              <span className="font-mono font-medium text-primary">{split.pace}/km</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Distance and time are read-only for outdoor runs (GPS-tracked) */}
                  {routeLine.length === 0 && (
                    <>
                      <Separator />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-distance" className="text-xs">{t('components.editWorkout.editDistance')}</Label>
                          <Input
                            id="edit-distance"
                            type="number"
                            step="0.1"
                            value={totalDistance}
                            onChange={(e) => setTotalDistance(e.target.value)}
                            className="bg-background/50 h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-time" className="text-xs">{t('components.editWorkout.editTime')}</Label>
                          <Input
                            id="edit-time"
                            value={totalTime}
                            onChange={(e) => setTotalTime(e.target.value)}
                            className="bg-background/50 h-9"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Gym Workout Exercises */}
          {!isGpsCardioWorkoutType(workoutType) && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg font-normal">{t('components.editWorkout.exercises')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
                {/* Existing Exercises */}
                {exercises.map((exercise, exerciseIndex) => (
                  <div key={`${exercise.name}-${exerciseIndex}`} className="p-3 sm:p-4 bg-background/20 rounded-lg border border-border/30">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-3">
                      <h4 className="font-medium text-sm sm:text-base">{exercise.name}</h4>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedExerciseForProgress(exercise.name);
                            setShowProgressModal(true);
                          }}
                          className="text-xs h-8"
                        >
                          {t('components.editWorkout.progress')}
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveExercise(exerciseIndex, 'up')}
                            disabled={exerciseIndex === 0}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                          >
                            <ChevronUp className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveExercise(exerciseIndex, 'down')}
                            disabled={exerciseIndex === exercises.length - 1}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                          >
                            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExercise(exerciseIndex)}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                        >
                          <X className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Individual Sets */}
                    <div className="space-y-2 sm:space-y-3">
                      {exercise.sets.map((set, setIndex) => (
                        <div key={set.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-background/30 rounded-lg">
                          <div className="flex items-center justify-between sm:justify-start gap-2 sm:min-w-[80px]">
                            <span className="text-xs sm:text-sm font-medium">{t('components.editWorkout.setLabel', { number: setIndex + 1 })}</span>
                            <Button
                              variant={set.completed ? "default" : "outline"}
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => completeSet(exerciseIndex, setIndex)}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-1 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={set.reps || ""}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => updateSet(exerciseIndex, setIndex, 'reps', e.target.value ? parseInt(e.target.value) : null)}
                                className="bg-background/50 h-8 w-14 sm:w-16 text-xs sm:text-sm text-center"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{t('components.editWorkout.repsUnit')}</span>
                            </div>
                            
                            <span className="text-muted-foreground">×</span>
                            
                            <div className="flex items-center gap-1">
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={set.weight || ""}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => {
                                  const value = e.target.value.replace(',', '.');
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    const parsed = value ? Number(value) : null;
                                    updateSet(
                                      exerciseIndex,
                                      setIndex,
                                      'weight',
                                      parsed !== null && Number.isFinite(parsed) ? parsed : null
                                    );
                                  }
                                }}
                                className="bg-background/50 h-8 w-20 text-sm text-center"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground">{weightLabel}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => addSet(exerciseIndex)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            {exercise.sets.length > 1 && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => removeSet(exerciseIndex, setIndex)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Exercise Notes */}
                    <div className="mt-3">
                      <Label className="text-xs">{t('components.editWorkout.notesLabel')}</Label>
                      <Textarea
                        value={exercise.notes || ""}
                        onChange={(e) => {
                          setExercises(prev => prev.map((ex, index) => 
                            index === exerciseIndex ? { ...ex, notes: e.target.value } : ex
                          ));
                        }}
                        className="bg-background/50 h-16 text-sm mt-1"
                        placeholder={t('components.editWorkout.notesPlaceholder')}
                      />
                    </div>
                  </div>
                ))}

                <Separator />

                {/* Add New Exercise */}
                <div className="space-y-4 p-4 border border-border/50 rounded-lg">
                  <h5 className="font-medium">{t('components.editWorkout.addExerciseHeader')}</h5>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder={t('components.editWorkout.exerciseNamePlaceholder')}
                      value={newExercise.name}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      placeholder={t('components.editWorkout.equipmentPlaceholder')}
                      value={newExercise.equipment}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, equipment: e.target.value }))}
                      className="bg-background/50"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      type="number"
                      placeholder={t('components.editWorkout.setsPlaceholder')}
                      value={newExercise.sets}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, sets: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      type="number"
                      placeholder={t('components.editWorkout.repsPlaceholder')}
                      value={newExercise.reps}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, reps: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder={t('components.editWorkout.weightPlaceholder')}
                      value={newExercise.weight_kg}
                      onChange={(e) => {
                        const value = e.target.value.replace(',', '.');
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewExercise(prev => ({ ...prev, weight_kg: value }));
                        }
                      }}
                      className="bg-background/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('components.editWorkout.muscleGroupsLabel')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {muscleGroups.map((group) => (
                        <Badge
                          key={group}
                          variant={newExercise.muscle_groups.includes(group) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => handleMuscleGroupToggle(group)}
                        >
                          {group}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button onClick={addExercise} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('components.editWorkout.addExercise')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-2">
                <Label htmlFor="edit-notes" className="text-sm">{t('components.editWorkout.notesLabel')}</Label>
                <Textarea
                  id="edit-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-background/50 min-h-[80px] sm:min-h-[100px] text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4">
            <Button
              onClick={saveWorkout}
              disabled={saving || !title.trim()}
              className="flex-1 bg-gradient-primary text-black h-10 text-sm"
            >
              {saving ? t('components.editWorkout.saving') : t('components.editWorkout.saveChanges')}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="default" className="h-10 text-sm">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('components.editWorkout.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('components.editWorkout.deleteDialogTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('components.editWorkout.deleteDialogDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteWorkout} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    {t('components.editWorkout.deleteDialogConfirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={handleClose} className="h-10 text-sm">
              {t('common.cancel')}
            </Button>
          </div>
        </div>

        {/* Exercise Progress Modal */}
        <ExerciseProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          exerciseName={selectedExerciseForProgress}
        />
      </DialogContent>
    </Dialog>
  );
};
